import type { ReactElement } from "react";
export interface PaginationProps {
  readonly currentPage: number;
  readonly totalPages: number;
  readonly hasMore: boolean;
  readonly onPageChange: (page: number) => void;
  readonly maxVisible?: number;
}

/**
 * Generic pagination component with Previous/Next and numbered page buttons
 * Supports ellipsis for large page ranges
 *
 * @param currentPage - The current active page (1-indexed)
 * @param totalPages - Total number of pages available
 * @param hasMore - Whether there are more pages beyond totalPages (for infinite scroll scenarios)
 * @param onPageChange - Callback when a page number is clicked
 * @param maxVisible - Maximum number of page buttons to show (default: 7)
 *
 * @example
 * ```tsx
 * <Pagination
 *   currentPage={2}
 *   totalPages={10}
 *   hasMore={true}
 *   onPageChange={(page) => navigate(`?page=${page}`)}
 * />
 * ```
 */
export function Pagination({
  currentPage,
  totalPages,
  hasMore,
  onPageChange,
  maxVisible = 7,
}: PaginationProps): ReactElement | null {
  if (totalPages <= 1) return null;

  const pages: number[] = [];
  let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
  const endPage = Math.min(totalPages, startPage + maxVisible - 1);

  if (endPage - startPage < maxVisible - 1) {
    startPage = Math.max(1, endPage - maxVisible + 1);
  }

  for (let i = startPage; i <= endPage; i++) {
    pages.push(i);
  }

  return (
    <div className="flex items-center justify-center gap-2 mt-12">
      <button
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        className="px-4 py-2 rounded-lg bg-neutral-900/25 text-white hover:bg-neutral-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        Previous
      </button>

      {startPage > 1 && (
        <>
          <button
            onClick={() => onPageChange(1)}
            className="px-4 py-2 rounded-lg bg-neutral-900/25 text-white hover:bg-neutral-800 transition-colors"
          >
            1
          </button>
          {startPage > 2 && <span className="text-neutral-500">...</span>}
        </>
      )}

      {pages.map((page) => (
        <button
          key={page}
          onClick={() => onPageChange(page)}
          className={`px-4 py-2 rounded-lg transition-colors ${
            page === currentPage
              ? "bg-lumi-accent text-black font-semibold"
              : "bg-neutral-900 text-white hover:bg-neutral-800"
          }`}
        >
          {page}
        </button>
      ))}

      {endPage < totalPages && (
        <>
          {endPage < totalPages - 1 && <span className="text-neutral-500">...</span>}
          <button
            onClick={() => onPageChange(totalPages)}
            className="px-4 py-2 rounded-lg bg-neutral-900 text-white hover:bg-neutral-800 transition-colors"
          >
            {totalPages}
          </button>
        </>
      )}

      <button
        onClick={() => onPageChange(currentPage + 1)}
        disabled={!hasMore}
        className="px-4 py-2 rounded-lg bg-neutral-900 text-white hover:bg-neutral-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        Next
      </button>
    </div>
  );
}
